import React, { useMemo, useState } from "react";
import { GiSnail } from "react-icons/gi";
import { FaPaintBrush, FaStar } from "react-icons/fa";
import { Box, FlexBox, Image } from "../primitives";
import { LabelStyled, ScrollStyles } from "../common/common.style";
import { useAddCommas } from "../../utils";
import { FiEdit2 } from "react-icons/fi";
import { InfoTooltip } from "../common/InfoTooltip/InfoTooltip";
import { CARD_CONDITIONS_CARDTRADER, CARD_LANGUAGES } from "../../constant";
import {
  CardTraderConditionValueType,
  CardTraderLanguageValueType,
  ProductPriceDataProductCardTraderType,
  ProductPriceDataUserExtraDataType,
} from "magic-shared-types";
import { CountryDropdown } from "../common/CountryDropdown.tsx/CountryDropdown";
import { LanguageDropdown } from "../common/LanguageDropdown/LanguageDropdown";
import { ConditionDropdown } from "../common/ConditionDropdown/ConditionDropdown";
import { CardSelected } from "../../type";
import { selectGame } from "../../store/slices/global";
import { useDispatch, useSelector } from "react-redux";
import { selectCountry, setCountry } from "../../store/slices/modalPricing";
import { getMatchingConditionsCardTrader } from "../../utils/condition";
import { TableRow } from "./modalPricing.styled";
import { ReversIcon } from "../common/CardsIconExtra/ReversIcon";
import { FirstEditionIcon } from "../common/CardsIconExtra/FirstEditionIcon";
import { CheckBox } from "../common/CheckBox/CheckBox";
import dayjs from "dayjs";

interface CardTraderTableProps {
  cardTraderCards: ProductPriceDataProductCardTraderType[];
  userExtraData?: Record<number, ProductPriceDataUserExtraDataType>;
  cardSelected: CardSelected;
  setCardsSelected: (arg1: CardSelected) => void;
  editedAt?: string;
}

export const CardTraderTable = ({
  cardTraderCards,
  userExtraData,
  cardSelected,
  setCardsSelected,
  editedAt,
}: CardTraderTableProps) => {
  const [language, setLanguage] = useState<CardTraderLanguageValueType>(
    cardSelected.language
  );
  const [condition, setCondition] = useState(cardSelected.condition);
  const [isReverseHolo, setIsReverseHolo] = useState(cardSelected.isReversed);
  const [isFirstEdition, setIsFirstEdition] = useState(
    cardSelected.isFirstEdition
  );
  const addCommas = useAddCommas();
  const game = useSelector(selectGame);
  const country = useSelector(selectCountry);
  const dispatch = useDispatch();
  const hasFirstEdition = ["yugioh", "pokemon"].includes(game.card_trader_game);
  const hasReverse = ["pokemon"].includes(game.card_trader_game);

  console.log("isReverseHolo", isReverseHolo);

  const filteredCardTraderCards = useMemo(() => {
    let cardTraderCardsToFilter = [...cardTraderCards];

    if (language) {
      cardTraderCardsToFilter = cardTraderCardsToFilter.filter(
        (product) =>
          language ===
          product?.properties_hash?.[`${game.card_trader_game}_language`]
      );
    }

    if (condition) {
      cardTraderCardsToFilter = cardTraderCardsToFilter.filter((product) =>
        getMatchingConditionsCardTrader(condition).includes(
          product.properties_hash?.condition as CardTraderConditionValueType
        )
      );
    }

    if (country) {
      cardTraderCardsToFilter = cardTraderCardsToFilter.filter((product) =>
        country
          ?.toLowerCase()
          .includes(product.user?.country_code?.toLowerCase())
      );
    }

    cardTraderCardsToFilter = cardTraderCardsToFilter.filter(
      (product) =>
        product?.properties_hash?.pokemon_reverse === isReverseHolo ||
        (!product?.properties_hash?.pokemon_reverse && !isReverseHolo)
    );

    cardTraderCardsToFilter = cardTraderCardsToFilter.filter(
      (product) =>
        product.properties_hash.first_edition === isFirstEdition ||
        (!product.properties_hash.first_edition && !isFirstEdition)
    );

    return cardTraderCardsToFilter;
  }, [
    cardTraderCards,
    language,
    condition,
    country,
    game,
    isReverseHolo,
    isFirstEdition,
  ]);

  return (
    <>
      <FlexBox css={{ gap: 10, marginTop: 20 }}>
        <a
          href={`https://www.cardtrader.com/cards/${cardSelected.id}`}
          target="_blank"
          rel="noreferrer"
        >
          View on Cardtrader
        </a>
        {editedAt && <Box>{dayjs(editedAt).format("hh:mm DD-MM-YYYY")}</Box>}
      </FlexBox>
      <FlexBox css={{ marginTop: 20, gap: 20 }}>
        <LanguageDropdown
          language={language}
          setLanguageValue={(lang) => setLanguage(lang)}
        />
        <CountryDropdown
          country={country}
          setCountry={(aCountry) => {
            dispatch(setCountry(aCountry));
          }}
        />
        <ConditionDropdown
          condition={condition}
          setCondition={(aCondition) => setCondition(aCondition)}
        />
        {hasReverse && (
          <FlexBox css={{ flexDirection: "column", gap: 10 }}>
            <LabelStyled>Reverse Holo</LabelStyled>
            <CheckBox
              checked={isReverseHolo}
              setChecked={(checked: boolean) => {
                setIsReverseHolo(checked);
              }}
            />
          </FlexBox>
        )}
        {hasFirstEdition && (
          <FlexBox css={{ flexDirection: "column", gap: 10 }}>
            <LabelStyled>First Edition</LabelStyled>
            <CheckBox
              checked={isFirstEdition}
              setChecked={(checked: boolean) => {
                setIsFirstEdition(checked);
              }}
            />
          </FlexBox>
        )}
      </FlexBox>
      <Box css={{ overflow: "auto", marginTop: 20, ...ScrollStyles }}>
        <Box css={{ height: "50vh" }}>
          {filteredCardTraderCards?.map((product, index) => {
            const userData = userExtraData?.[product?.user?.id];
            const isSlow = (userData?.ct_zero_avg_shipping_days || 0) > 14;
            const cardLanguage = CARD_LANGUAGES?.find(
              (lang) =>
                lang.value ===
                product?.properties_hash?.[`${game.card_trader_game}_language`]
            );
            const cardCondition = CARD_CONDITIONS_CARDTRADER?.find(
              (cond) => cond.value === product?.properties_hash?.condition
            );

            return (
              <TableRow
                key={`${product.user}-${index}`}
                isEven={!!(index % 2)}
                onClick={() => {
                  setCardsSelected({
                    ...cardSelected,
                    price: product?.price?.non_layered_price_formatted?.split(
                      "€"
                    )?.[1]
                      ? parseFloat(
                          product.price.non_layered_price_formatted.split(
                            "€"
                          )[1]
                        )
                      : product.price.cents / 100,
                  });
                }}
              >
                <Box css={{ width: "50%" }}>
                  <FlexBox css={{ gap: 5 }}>
                    <Box>{product?.user?.username}</Box>
                    <Image
                      src={`https://flagcdn.com/${product?.user?.country_code?.toLocaleLowerCase()}.svg`}
                      alt={product?.user?.country_code}
                      css={{ width: 30 }}
                    />
                  </FlexBox>
                  {userData?.feedbacks_count && (
                    <FlexBox
                      css={{
                        alignItems: "center",
                        gap: 2,
                        fontSize: 14,
                        marginTop: 5,
                      }}
                    >
                      <FaStar size={12} color="grey" />
                      {addCommas(userData.feedbacks_count)}
                    </FlexBox>
                  )}
                  {isSlow && (
                    <FlexBox
                      css={{
                        fontSize: 12,
                        fontWeight: "bold",
                        gap: 10,
                        alignItems: "center",
                      }}
                    >
                      <GiSnail color="#8B4513" size={20} />
                      Slow seller
                    </FlexBox>
                  )}
                </Box>
                <FlexBox
                  css={{ gap: 5, width: "30%", justifyContent: "start" }}
                >
                  {cardCondition && (
                    <Box
                      css={{
                        padding: 5,
                        background: cardCondition?.color,
                        borderRadius: 5,
                      }}
                    >
                      {cardCondition?.code}
                    </Box>
                  )}
                  {cardLanguage && (
                    <Image
                      src={`${cardLanguage?.flag}`}
                      alt={cardLanguage?.label}
                      css={{ width: 30 }}
                    />
                  )}
                  {product?.properties_hash?.pokemon_reverse && (
                    <InfoTooltip content="Reverse Holo">
                      <Box>
                        <ReversIcon />
                      </Box>
                    </InfoTooltip>
                  )}
                  {product?.properties_hash?.first_edition && (
                    <InfoTooltip content="First Edition">
                      <Box>
                        <FirstEditionIcon />
                      </Box>
                    </InfoTooltip>
                  )}
                  {product?.properties_hash?.[
                    `${game.card_trader_game}_foil`
                  ] && (
                    <InfoTooltip content="Foil">
                      <FaStar size={24} color="#FFD700" />
                    </InfoTooltip>
                  )}
                  {product?.properties_hash?.signed && (
                    <InfoTooltip content="Signed">
                      <FiEdit2 size={24} />
                    </InfoTooltip>
                  )}
                  {product?.properties_hash?.altered && (
                    <InfoTooltip content="Altered">
                      <FaPaintBrush size={24} color="brown" />
                    </InfoTooltip>
                  )}
                  {product?.user?.can_sell_via_hub && (
                    <Box
                      css={{
                        color: "white",
                        background: "$primary",
                        padding: "5px 10px",
                        borderRadius: 8,
                      }}
                    >
                      CT ZERO
                    </Box>
                  )}
                </FlexBox>
                <FlexBox css={{ gap: 5, width: "30%", justifyContent: "end" }}>
                  <Box>{product?.quantity}x</Box>
                  {product?.price?.non_layered_price_formatted && (
                    <Box>
                      {addCommas(product.price.non_layered_price_formatted)}
                    </Box>
                  )}
                  {product?.price?.cents && (
                    <Box>({addCommas(product.price.cents / 100)})</Box>
                  )}
                </FlexBox>
              </TableRow>
            );
          })}
        </Box>
      </Box>
    </>
  );
};
